import { useMutation, useQueryClient } from '@tanstack/react-query';
import { planApi } from '@src/apis/admin';
import type { ApplyPlanRequestModel, PlanType as ApiPlanType } from '@uniquegood/realworld-admin-interface';
import type { PlanNoPaymentFormData } from '@src/pages/Studio/Channel/plan-no-payment/plan-no-payment-form-modal';

/**
 * 결제 없이 요금제 적용/연장 mutation
 *
 * @description
 * - 채널에 요금제를 결제 없이 적용하거나 연장합니다.
 * - PlanApi.applyPlan을 사용하여 실제 API를 호출합니다.
 * - 성공 시 관련 쿼리들을 무효화하여 데이터를 갱신합니다.
 */
export const useApplyPlan = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationKey: ['applyPlan'],
        mutationFn: async ({
            channelId,
            formData,
            mode,
        }: {
            channelId: string;
            formData: PlanNoPaymentFormData;
            mode: 'apply' | 'extend';
        }) => {
            // PlanType을 API 형식으로 변환 (첫 글자만 대문자)
            const planTypeMap: Record<string, ApiPlanType> = {
                'free': 'Free' as ApiPlanType,
                'pro': 'Pro' as ApiPlanType,
                'enterprise': 'Enterprise' as ApiPlanType,
                'teacher': 'Teacher' as ApiPlanType,
                'class': 'Class' as ApiPlanType,
                'realworld': 'Realworld' as ApiPlanType,
            };

            const apiPlanType = planTypeMap[formData.planType];
            if (!apiPlanType) {
                throw new Error(`Invalid plan type: ${formData.planType}`);
            }

            const requestBody: ApplyPlanRequestModel = {
                planType: apiPlanType,
                useMonths: formData.durationMonths ? parseInt(formData.durationMonths, 10) : undefined,
                memo: formData.note || '',
            };

            const { data } = await planApi.applyPlan(
                channelId,
                '1.0', // x-rwd-api-version
                requestBody
            );

            return { data, channelId, mode };
        },
        onSuccess: (response) => {
            // 관련 쿼리 무효화하여 재조회 유도
            queryClient.invalidateQueries({ queryKey: ['plan-history', response.channelId] });
            queryClient.invalidateQueries({ queryKey: ['current-payment-request', response.channelId] });
            queryClient.invalidateQueries({ queryKey: ['channel-detail', response.channelId] });
            queryClient.invalidateQueries({ queryKey: ['channels'] });
        },
    });
};

